import { useQuery } from '@tanstack/react-query';

import { getStripeConnectStatus } from '@/api/communities';

import { deriveConnectReadiness, type ConnectReadiness } from './connect-readiness';

export { deriveConnectReadiness } from './connect-readiness';
export type { ConnectReadiness } from './connect-readiness';

/**
 * Live Stripe Connect readiness for a community.
 *
 * The session `community` object (from `useUser`) carries connect flags that are
 * not refreshed when a manager connects/disconnects Stripe, which lets stale UI
 * offer actions the backend will reject. Gate payment-related UI on this hook
 * instead so it always matches the authoritative `connect/status` endpoint.
 */
export function useConnectStatus(communityId?: string | number | null): ConnectReadiness {
  const id = communityId != null ? String(communityId) : '';

  // Shares the cache key used by the community payments settings page so its
  // connect/disconnect mutations (which invalidate this key) instantly refresh
  // every payment-gated surface in the app.
  const { data, isLoading } = useQuery({
    queryKey: ['stripe-connect-status', id],
    queryFn: () => getStripeConnectStatus(id),
    enabled: id !== '',
    staleTime: 30_000,
  });

  return {
    status: data,
    isLoading,
    ...deriveConnectReadiness(data),
  };
}
